import { differenceInMilliseconds } from 'date-fns';

interface Dependencies {
  storage: Storage;
  now: () => Date;
}

export interface Session {
  accoId: number;
  timeSpent: number;
}

export const SESSION_START_PREFIX = 'session-start-' as const;
export const SESSION_TIME_PREFIX = 'session-time-' as const;

export const createSessionRecorder = (dependencies: Partial<Dependencies> = {}) => {
  const { storage = localStorage, now = () => new Date() } = dependencies as Dependencies;
  let _accommodationId = 0;

  const setAccommodationId = (id: number) => {
    _accommodationId = id;
  };

  const getAccommodationId = () => {
    return _accommodationId;
  };

  const getStoredStartTime = (): Date => {
    const startTime = parseInt(storage.getItem(`${SESSION_START_PREFIX}${getAccommodationId()}`) ?? '0', 10);
    return new Date(startTime);
  };

  const getPreviousStoredElapsedTime = (): number => {
    return parseInt(storage.getItem(`${SESSION_TIME_PREFIX}${getAccommodationId()}`) ?? '0', 10);
  };

  const getElapsedTimeFrom = (startTime: Date): number => {
    return differenceInMilliseconds(now(), startTime);
  };

  const store = (timeSpent: number) => {
    storage.setItem(`${SESSION_TIME_PREFIX}${getAccommodationId()}`, timeSpent.toString());
  };

  const cleanupStoredStartDateTime = () => {
    storage.removeItem(`${SESSION_START_PREFIX}${getAccommodationId()}`);
  };

  function storeStartDateTime() {
    storage.setItem(`${SESSION_START_PREFIX}${getAccommodationId()}`, now().getTime().toString());
  }

  function isAlreadyStarted() {
    return getStoredStartTime().getTime() !== 0;
  }

  const start = (accommodationId: number) => {
    setAccommodationId(accommodationId);

    if (isAlreadyStarted()) {
      return;
    }

    storeStartDateTime();
  };

  const stop = (accommodationId: number) => {
    setAccommodationId(accommodationId);
    const startTime = getStoredStartTime();

    if (startTime.getTime() === 0) {
      return;
    }

    const prevExistingElapsedTime = getPreviousStoredElapsedTime();
    const currentElapsedTime = getElapsedTimeFrom(startTime);
    const timeSpent = prevExistingElapsedTime + currentElapsedTime;

    store(timeSpent);
    cleanupStoredStartDateTime();
  };

  const extractAccommodationIdFromStoredKey = (key: string) => {
    return key.replace(SESSION_TIME_PREFIX, '');
  };

  const getAllSessions = (): Session[] => {
    const allKeys = Object.keys(storage);
    const sessionKeys = allKeys.filter((key) => key.startsWith(SESSION_TIME_PREFIX));

    return sessionKeys.reduce((sessions: Session[], key: string) => {
      const accoId = extractAccommodationIdFromStoredKey(key);
      const timeSpent = storage.getItem(key);
      if (!timeSpent || timeSpent === '0' || parseInt(timeSpent, 10) === 0) {
        return sessions;
      }
      sessions.push({ accoId: parseInt(accoId, 10), timeSpent: parseInt(timeSpent, 10) });
      return sessions;
    }, []);
  };

  return {
    start,
    stop,
    getAllSessions,
  };
};
